import React from 'react'
import styled from 'styled-components'

import { PopOverItemType } from 'components'
import MoreOptionsPopOver from 'components/MoreOptionsPopOver'
import Table, { TableProps, TableColumn, TableVariant, TableVariantType } from 'components/Table'
import Filters from './Filters'

import { colors } from 'utils/colors'
import { FixMeAny, UnknownRecord } from 'types/global'
import { isInlineTable } from 'components/Table/helpers'

type AdvancedTableProps<D extends UnknownRecord, T = FixMeAny> = TableProps<D> & {
  advTableActions?: PopOverItemType[]
  bgColor?: keyof typeof colors
  columns: Array<TableColumn<D>>
  customHeader?: React.ReactNode
  customMoreOptions?: React.ReactNode | JSX.Element
  data: Array<D>
  filters?: React.ReactNode
  moreOptionsValue?: PopOverItemType<T>
  onSelectMoreOption?: (value?: PopOverItemType<T>) => void
  showTableHeader?: boolean
  searchBar?: React.ReactNode
  customPanel?: React.ReactNode
  isLoading?: boolean
  style?: React.CSSProperties
  variant?: TableVariant
  activeRowIndex?: number
}

const AdvancedTable = <D extends UnknownRecord = UnknownRecord>({
  advTableActions,
  bgColor = 'white',
  customMoreOptions,
  filters,
  moreOptionsValue,
  onSelectMoreOption,
  customHeader,
  showTableHeader = true,
  searchBar,
  variant = TableVariantType.default,
  pageNumber = 0,
  pageSize = 100,
  ...props
}: AdvancedTableProps<D>) => (
  <>
    {showTableHeader && customHeader}
    <FilterSectionContainer bgColor={bgColor} borderless={props.borderless} variant={variant}>
      <FiltersWrapper>{filters}</FiltersWrapper>
      <ButtonsWrapper>
        {searchBar}
        {(customMoreOptions || advTableActions) && (
          <MoreOptionsPopOver
            customMenuList={customMoreOptions}
            ml="8px"
            options={advTableActions}
            onSelect={onSelectMoreOption}
            value={moreOptionsValue}
            isPortaled={false}
            iconButton
          />
        )}
      </ButtonsWrapper>
    </FilterSectionContainer>
    <Table<D> {...props} pageNumber={pageNumber} pageSize={pageSize} variant={variant} />
  </>
)

const ButtonsWrapper = styled.div`
  align-items: center;
  display: flex;
`

const FiltersWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: left;
  gap: 5px;
`

const FilterSectionContainer = styled.div<{
  bgColor: keyof typeof colors
  borderless?: boolean
  variant: TableVariant
}>`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ variant }) => (isInlineTable(variant) ? '8px 0px' : '10px 15px')};
  background-color: ${({ borderless, theme, bgColor }) =>
    borderless ? (bgColor ? theme.colors[bgColor] : theme.colors.gray1) : theme.colors[bgColor]};
  gap: 5px;
  border: ${({ borderless, theme }) => (borderless ? 'none' : `1px solid ${theme.colors.gray3}`)};
  border-bottom: none;
  ${({ variant }) => isInlineTable(variant) && 'border:none'};
`

export const Filter = Filters

export default AdvancedTable
